import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify'; // Import toast
import { getBackendUrl } from '../apiConfig';
import './SubscriptionManage.css';

// Mapping of price IDs to human-readable plan details
const planMapping = {
    'price_1RatOcRvHo620RneKGNASUqx': 'Standard, $49/month',
    'price_1RatOWRvHo620RneHGh5k1gz': 'Pro, $89/month',
    'price_1RatOWRvHo620RneHwIhiuL0': 'Pro, $960/year',
    'price_1RatOcRvHo620RneWbkgX4jz': 'Standard, $528/year',
    'price_1RatOcRvHo620RnewbkgX4jz': 'Standard, $528/year', // legacy typo
};

const SubscriptionManage = () => {
    const user = auth.currentUser;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [hasUsedTrial, setHasUsedTrial] = useState(false); // Add trial history state

    // Redirect to login if user is not authenticated
    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        const fetchSubscriptionData = async () => {
            try {
                setLoading(true);
                const userDocRef = doc(db, 'users', user.uid);
                const userDoc = await getDoc(userDocRef);
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setSubscriptionData(data.subscription || null);
                    setHasUsedTrial(
                        data.subscription?.hasUsedTrial === true || data.hasUsedTrial === true
                    );
                } else {
                    setError('User subscription data not found.');
                }
            } catch (err) {
                setError('Failed to load subscription details: ' + err.message);
                console.error('Fetch subscription error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchSubscriptionData();
    }, [user, navigate]);

    const backendUrl = getBackendUrl();

    // Handle redirect to Stripe Customer Portal
    const handleManageSubscription = async () => {
        if (!user || !subscriptionData?.customerId) {
            setError('No subscription found to manage.');
            return;
        }

        setLoading(true);
        setError('');
        setMessage('');

        try {
            const response = await fetch(`${backendUrl}/create-customer-portal-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ customerId: subscriptionData.customerId }),
            });

            if (!response.ok) {
                const errBody = await response.json().catch(() => ({}));
                throw new Error(errBody.error || `Failed to create Customer Portal session (${response.status})`);
            }

            const { url } = await response.json();
            window.location.href = url; // Redirect to Stripe Customer Portal
        } catch (err) {
            setError('Failed to access subscription management: ' + err.message);
            console.error('Portal error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Handle starting a new subscription (non-trial)
    const handleStartNewSubscription = async (priceId) => {
        if (hasUsedTrial && priceId === 'price_with_trial') { // Replace with your trial priceId
            toast.error(
                <>
                    You have already used your trial subscription. Please select a paid plan or reactivate your existing subscription.{' '}
                    <a href="/subscription/manage">Manage your subscription</a>.
                </>,
                { autoClose: 5000, className: 'toastify' }
            );
            return;
        }

        setLoading(true);
        try {
            const email = user.email?.trim();
            if (!email) {
                throw new Error('Your account has no email on file. Please update your profile.');
            }
            const response = await fetch(`${backendUrl}/create-checkout-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: user.uid, priceId, email }),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(body.error || `Failed to start checkout (${response.status})`);
            }
            const checkoutUrl = body.url;
            if (checkoutUrl && typeof checkoutUrl === 'string') {
                window.location.assign(checkoutUrl);
                return;
            }
            throw new Error('Checkout session did not return a URL.');
        } catch (error) {
            console.error('Checkout error:', error);
            toast.error(
                <>
                    Failed to start checkout: {error.message}.{' '}
                    <a href="/subscription/manage">Try again</a>.
                </>,
                { autoClose: 5000, className: 'toastify' }
            );
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return null; // Render nothing while redirecting
    }

    const displayPlan = subscriptionData?.plan ? planMapping[subscriptionData.plan] || 'Unknown Plan' : 'N/A';
    const displayEmail = user.email || 'N/A';
    // Portal requires a Stripe customer; pending = registered, not yet subscribed
    const canOpenCustomerPortal =
        !!subscriptionData?.customerId &&
        subscriptionData.status !== 'canceled' &&
        subscriptionData.status !== 'pending';

    return (
        <div className="subscription-page">
            <div className="subscription-container">
                <h2 className="subscription-title">Manage Your Subscription</h2>
                {loading && <p>Loading...</p>}
                {error && <p className="subscription-error">{error}</p>}
                {message && <p className="subscription-message">{message}</p>}
                {subscriptionData ? (
                    <div className="plan-card">
                        <h3>Subscription Details</h3>
                        <p>Status: {subscriptionData.status || 'Inactive'}</p>
                        {canOpenCustomerPortal ? (
                            <>
                                <p>Plan: {displayPlan}</p>
                                <p>Email: {displayEmail}</p>
                                {subscriptionData.status === 'paused' && (
                                    <p className="subscription-message">
                                        Your subscription is paused. Reactivate it to continue using the service.
                                    </p>
                                )}
                                <button
                                    onClick={handleManageSubscription}
                                    className="subscribe-btn"
                                    disabled={loading}
                                >
                                    {loading ? 'Loading...' : 'Manage Subscription'}
                                </button>
                            </>
                        ) : (
                            <>
                                <p className="manage-link">
                                    Your subscription is inactive.{' '}
                                    {hasUsedTrial ? (
                                        'You have already used your trial. Please select a paid plan.'
                                    ) : (
                                        'Start a new subscription.'
                                    )}
                                </p>
                                {hasUsedTrial ? (
                                    <>
                                        <button
                                            onClick={() => handleStartNewSubscription('price_1RatOcRvHo620RneKGNASUqx')} // Standard monthly
                                            className="subscribe-btn"
                                            disabled={loading}
                                        >
                                            {loading ? 'Loading...' : 'Subscribe to Standard ($49/month)'}
                                        </button>
                                        <button
                                            onClick={() => handleStartNewSubscription('price_1RatOWRvHo620RneHGh5k1gz')} // Pro monthly
                                            className="subscribe-btn"
                                            disabled={loading}
                                        >
                                            {loading ? 'Loading...' : 'Subscribe to Pro ($89/month)'}
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => navigate('/subscription')}
                                        className="link-btn"
                                    >
                                        Subscribe now
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                ) : !error && !loading && (
                    <p className="manage-link">
                        No subscription data found.{' '}
                        {hasUsedTrial ? (
                            'You have already used your trial. Please select a paid plan.'
                        ) : (
                            <button onClick={() => navigate('/subscription')} className="link-btn">
                                Subscribe now
                            </button>
                        )}
                        {hasUsedTrial && (
                            <>
                                <button
                                    onClick={() => handleStartNewSubscription('price_1RatOcRvHo620RneKGNASUqx')} // Standard monthly
                                    className="subscribe-btn"
                                    disabled={loading}
                                >
                                    {loading ? 'Loading...' : 'Subscribe to Standard ($49/month)'}
                                </button>
                                <button
                                    onClick={() => handleStartNewSubscription('price_1RatOWRvHo620RneHGh5k1gz')} // Pro monthly
                                    className="subscribe-btn"
                                    disabled={loading}
                                >
                                    {loading ? 'Loading...' : 'Subscribe to Pro ($89/month)'}
                                </button>
                            </>
                        )}
                    </p>
                )}
                <p className="manage-link">
                    Return to <button onClick={() => navigate('/settings')} className="link-btn">Settings</button>
                </p>
            </div>
        </div>
    );
};

export default SubscriptionManage;